/** MI DOMINO — client error reporter.
 *  Uncaught errors and unhandled promise rejections are POSTed to
 *  /api/client-error so production bugs show up in the founder's admin panel
 *  (🐞 Errors) instead of arriving as screenshots. Deduped + throttled so a
 *  crash loop can't flood the server; never throws itself. */
import { LANG } from './i18n';
import { API_BASE } from '../net/base';

// token read straight from storage: this module must not depend on main.ts
// (it is installed before main's body runs, and must work if main throws)
const token = (): string => { try { return localStorage.getItem('mdtoken') || ''; } catch { return ''; } };

const seen = new Map<string, number>();   // message → times reported this session
let sentThisMinute = 0;
let minuteStart = Date.now();

function post(payload: Record<string, string>): void {
  try {
    const now = Date.now();
    if (now - minuteStart > 60_000) { minuteStart = now; sentThisMinute = 0; }
    if (sentThisMinute >= 5) return;                      // ≤ 5 reports / minute
    const n = (seen.get(payload.msg) || 0) + 1;
    seen.set(payload.msg, n);
    if (n > 3) return;                                    // same error ≤ 3× / session
    sentThisMinute++;
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    const tk = token();
    if (tk) headers.Authorization = 'Bearer ' + tk;
    fetch(API_BASE + '/api/client-error', { method: 'POST', headers, body: JSON.stringify(payload), keepalive: true }).catch(() => {});
  } catch { /* reporting must never break the game */ }
}

function base(): Record<string, string> {
  return {
    url: location.pathname + location.hash,
    lang: LANG,
    screen: `${innerWidth || screen.width}x${innerHeight || screen.height}`,
  };
}

/** Report a caught-but-important error by hand (socket failures, etc.). */
export function reportError(msg: string, ctx?: string, err?: unknown): void {
  const stack = err && (err as Error).stack ? String((err as Error).stack) : '';
  post({ ...base(), msg: String(msg).slice(0, 500), ctx: ctx || '', stack: stack.slice(0, 4000) });
}

export function installErrorReporter(): void {
  if (!/^https?:/.test(location.protocol)) return;   // file:// dev opens have no server
  window.addEventListener('error', (ev) => {
    const err = ev.error as Error | undefined;
    const msg = (err && err.message) || ev.message || 'Error';
    const where = ev.filename ? ` @ ${ev.filename.split('/').pop()}:${ev.lineno}:${ev.colno}` : '';
    post({ ...base(), msg: String(msg).slice(0, 500), ctx: 'window.onerror' + where, stack: String((err && err.stack) || '').slice(0, 4000) });
  });
  window.addEventListener('unhandledrejection', (ev) => {
    const r = ev.reason as any;
    const msg = (r && r.message) || String(r || 'Unhandled rejection');
    post({ ...base(), msg: String(msg).slice(0, 500), ctx: 'unhandledrejection', stack: String((r && r.stack) || '').slice(0, 4000) });
  });
}
